import React, { useEffect, useMemo } from 'react';
import { PluckerEgeSwitch } from '@edu-frontend/modules/src/shared/ui/Tabs';
import { WIDGET_TYPE } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { sortBy } from 'lodash';
import { getExperimentFlags, Spin, useAppContext, useGeneratePath, useStaticUrls } from 'platform-components';
import { getUserId, getMe } from 'platform-components/src/components/user/selectors';
import { scrollToElement } from 'platform-components/utils';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import ym from 'react-yandex-metrika';
import {
    showUxFeedbackWidget,
    actions as feedbackActions,
} from '@edu-frontend/modules/src/shared/lib/uxFeedbackWidget';
import { wrapError } from '@edu-frontend/modules/src/shared/lib/error';
import { showOtklick } from '@edu-frontend/modules/src/shared/lib/otklickWidget';

import ClosingLessonModal from 'common.components/closing-lesson-modal/closing-lesson-modal';
import CopyLessonModal from 'common.components/copy-lesson-modal/copy-lesson-modal';
import { CustomLessonInfoModal } from 'common.components/custom-lesson-info-modal/custom-lesson-info-modal';
import { useAnchor } from 'common.components/hooks';
import { useUxFeedbackFeatures } from 'common.components/user/queries';
import { LessonViewWithAddSchoolControl as LessonView } from 'common.components/lesson-view.index/lesson-view_with-add-school-control';
import { SubscriptionModal } from 'common.components/lesson-view.subscription-modal/subscription-modal';
import { getCurrentClass, getIsPublicShowcase, getSavedOrFirstTeamId } from 'common.components/user/selectors';
import { WidgetView } from 'common.components/widgets.widget-view/widget-view';
import { actions as coursesActions } from 'store/courses/actions';
import { getCoursesLoadingStatus, getShouldLoadCourses } from 'store/courses/selectors';
import { getCurrentOrFirstTeamIdWithSubject, getIsClassCoursesRequestSuccess } from 'store/teacher-courses/selectors';
import { getSavedTeamId } from 'utils/saved-team-id';

import { useTeacherMainPage } from 'common.components/teacher-main-page/queries';
import { useCurrentGrade } from '../../../utils/use-current-grade';
import { lessonModalClose } from '../../library.store/actions';
import { PublicContent } from '../__public-content/main__public-content';

import './main__content.scss';
import { useBunkerProductTour } from 'hooks/useBunkerProductTour';
import { useLocalStorage } from 'platform-components/src/utils/hooks/useLocalStorage';

export const DEFAULT_SHOWCASE_GRADE = 5; // по умолчанию всегда выбран 5 грейд информатики
const SCROLL_SHIFT_HEIGHT = 64;

// 4 минуты
const uxfbTimeout = 60000 * 4;

// eslint-disable-next-line complexity
export const Content = () => {
    const dispatch = useDispatch();
    const { classId } = useParams<{ classId: string }>();
    const history = useHistory();

    const staticUrls = useStaticUrls();
    const generatePath = useGeneratePath();

    const teamId = classId ? Number(classId) : null;
    const team = useSelector((store) => getCurrentClass(store, { classId: teamId }));
    const savedOrFirstTeamId = useSelector(getSavedOrFirstTeamId);

    const isPublicShowCase = useSelector(getIsPublicShowcase);

    // получение грейда из localstorage осуществляем только для публичной витрины
    const grade = useCurrentGrade(DEFAULT_SHOWCASE_GRADE, !!savedOrFirstTeamId) ?? undefined;

    const shouldLoadCourses = useSelector((store) => getShouldLoadCourses(store, { classId: teamId }));
    const coursesLoadingStatus = useSelector((store) => getCoursesLoadingStatus(store, { classId: teamId }));
    const { data: pageData, isSuccess: pageDataIsSuccess } = useTeacherMainPage({
        grade,
    });

    const userId = useSelector(getUserId);
    const me = useSelector(getMe);

    const mainPage = pageData?.page;

    const isLoading = !pageDataIsSuccess || (Boolean(teamId) && coursesLoadingStatus !== LOADING_STATUSES.SUCCESS);

    const metrikaParams = {
        showcaseGrade: mainPage?.grade,
        showcaseId: mainPage?.id,
    };

    // логика привязанная к кадровому резерву
    const isClassCoursesLoaded = useSelector(getIsClassCoursesRequestSuccess);
    const teamIdWithInformatics = useSelector((storeState) =>
        getCurrentOrFirstTeamIdWithSubject(storeState, { subject: 'informatics' })
    );
    const isInformaticsTeacher = Boolean(teamIdWithInformatics);

    const { isRedirectLoggedInUser, ...otherQuery } = qs.parse(history.location.search);
    const { hr_tab } = otherQuery;

    // Параметр isRedirectLoggedInUser говорит что пользователя, получившего роль, надо редиректнуть на LabLibraryMain.
    // Так как мы уже здесь, то убираем его, чтобы исправить лишние редиректы
    if (isRedirectLoggedInUser) {
        history.replace(`${history.location.pathname}?${qs.stringify(otherQuery)}`);
    }

    useEffect(() => {
        if (teamId && shouldLoadCourses) {
            dispatch(coursesActions.getCoursesByTeamRequest({ teamId }));
        }
    }, [dispatch, shouldLoadCourses, teamId]);

    useEffect(() => {
        if (!teamId) {
            ym('reachGoal', 'teacher_main_visit_without_team', { grade, user_id: userId || null });
        }
    }, [grade, teamId, userId]);

    // Показываем UxFeedback, если у пользователя есть роль учителя
    const { data: uxFeedbackFeatures } = useUxFeedbackFeatures(Boolean(savedOrFirstTeamId));

    const appContext = useAppContext();
    const isProduction = appContext.environment === 'production';

    useEffect(() => {
        if (isInformaticsTeacher && uxFeedbackFeatures) {
            for (const eventName of uxFeedbackFeatures) {
                // Мы не сообщаем бэку, что показали определенный фидбек
                // То, что пользователь видит его лишь один раз
                // контролирует сам фидбэк

                if (isProduction) {
                    showUxFeedbackWidget(eventName);
                }

                showOtklick(eventName, (err) => {
                    window.Ya.Rum.logError(
                        { message: 'Failed to showOtklick', additional: { eventName } },
                        wrapError(err)
                    );
                });
            }
        }
    }, [uxFeedbackFeatures, isInformaticsTeacher, isProduction]);

    // Показываем фидбэк пользователям, которые за 4 минуты
    // не создали класса, но остались на платформе
    useEffect(() => {
        if (!savedOrFirstTeamId) {
            setTimeout(() => {
                if (!getSavedTeamId()) {
                    if (isProduction) {
                        showUxFeedbackWidget(feedbackActions.PUBLIC_PAGE_WITHOUT_CLASS);
                    }

                    showOtklick(feedbackActions.PUBLIC_PAGE_WITHOUT_CLASS, (err) => {
                        window.Ya.Rum.logError(
                            {
                                message: 'Failed to showOtklick',
                                additional: { eventName: feedbackActions.PUBLIC_PAGE_WITHOUT_CLASS },
                            },
                            wrapError(err)
                        );
                    });
                }
            }, uxfbTimeout);
        }
    }, [savedOrFirstTeamId, isProduction]);

    useEffect(() => {
        // если в урле нет teamId, напр учитель перешел по ссылке из письма
        if (!teamId) {
            let url;

            // параметр hr_tab означает вкладку виджета кадрового резерва, он есть только у учителей информатики
            // поэтому ждем isClassCoursesLoaded, чтобы найти класс с информатикой и редиректнуть в него
            if (hr_tab && !isClassCoursesLoaded) {
                return;
            }

            if (hr_tab && teamIdWithInformatics) {
                url = generatePath(staticUrls.labLibraryTeamMain, { classId: teamIdWithInformatics });
            } else if (savedOrFirstTeamId) {
                url = generatePath(staticUrls.labLibraryTeamMain, { classId: savedOrFirstTeamId });
            }

            if (url) {
                history.push(`${url}${history.location.search}`);
            }
        }
    }, [
        isClassCoursesLoaded,
        savedOrFirstTeamId,
        generatePath,
        history,
        hr_tab,
        staticUrls.labLibraryTeamMain,
        teamId,
        teamIdWithInformatics,
    ]);

    useEffect(() => {
        if (teamId && !team) {
            history.push(generatePath(staticUrls.labLibraryMain));
        }
    }, [generatePath, history, staticUrls.labLibraryMain, team, teamId]);

    // логика подскрола по слагу блока
    const anchorFromQuery = useAnchor();
    const anchorRef = React.useRef(null);
    useEffect(() => {
        if (!isLoading) {
            scrollToElement(anchorRef?.current, SCROLL_SHIFT_HEIGHT);
        }
    }, [isLoading, anchorRef]);

    const mappedBlocks = useMemo(() => {
        return sortBy(mainPage?.blocks, ({ order }) => order).map((block) => {
            if (block.type === WIDGET_TYPE.TEACHER_RESERVE && !isInformaticsTeacher) {
                return null;
            }

            const anchor = block.slug || block.type;
            const ref = anchorFromQuery === anchor ? anchorRef : undefined;

            return (
                <div className="library__widget-row" id={anchor} key={block.id} ref={ref}>
                    <WidgetView
                        isPublic={isPublicShowCase}
                        metrikaParams={{ ...metrikaParams, showcaseWidgetSlug: block.type }}
                        widget={block}
                    />
                </div>
            );
        });
    }, [mainPage?.blocks, isInformaticsTeacher, anchorFromQuery, isPublicShowCase, metrikaParams]);

    // логика показа product tour
    // Открываем тур только неавторизованным пользователями или учителям без классов
    const { start } = useBunkerProductTour({ name: 'teacher-onboarding' });
    const { get: getFromLocalStorage, set: setToLocalStorage } = useLocalStorage();

    const experimentFlags = useSelector(getExperimentFlags) as Record<string, unknown> | null;
    const showProductTourMainPage = getFromLocalStorage('showProductTourMainPage');

    useEffect(() => {
        if (!isLoading && showProductTourMainPage && (!userId || !savedOrFirstTeamId)) {
            start();
            setToLocalStorage('showProductTourMainPage', false);
        }
    }, [userId, isLoading, savedOrFirstTeamId, showProductTourMainPage, start, setToLocalStorage]);

    // если у учителя нет класса, открываем публичную витрину
    if (!savedOrFirstTeamId) {
        return <PublicContent isLoading={isLoading} widgets={mappedBlocks} />;
    }

    return (
        <div className="library__container">
            <div className="library">
                {isLoading ? (
                    <Spin center />
                ) : (
                    <>
                        {grade!! >= 5 && (
                            <PluckerEgeSwitch
                                metrikaParams={{
                                    puid: me?.id,
                                    user_id: me?.yauid,
                                    experiments: experimentFlags ? Object.keys(experimentFlags).join(',') : null,
                                }}
                                mix="library__ege-switch"
                                selected="plucker"
                                ym={ym}
                            />
                        )}
                        {mappedBlocks}
                    </>
                )}

                <LessonView
                    isCopyingEnabled={true}
                    onClose={() => dispatch(lessonModalClose())}
                    shouldCloseOnGoBack={true}
                />
                <CustomLessonInfoModal />
                <ClosingLessonModal />
                <CopyLessonModal />
                <SubscriptionModal />
            </div>
        </div>
    );
};
